import type { ColumnDef } from "@tanstack/react-table";
import { Badge } from "@/components/ui/badge";
import {
  DataTableColumnHeader,
  ImageCell,
  TextCell,
  ExpandCell,
  ActionsCell,
  makeSelectColumn,
} from "@/components/data-table";
import { msg } from "@lingui/core/macro";
import type { I18n } from "@lingui/core";
import type { MenuItemRow } from "./types";

export interface MenuItemRowCallbacks {
  onEdit: (row: MenuItemRow) => void;
  onDelete: (row: MenuItemRow) => void;
  /** Optional: publishing/category controls only apply to recipe-backed rows. */
  onPublish?: (row: MenuItemRow) => void;
  onUnpublish?: (row: MenuItemRow) => void;
  onChangeCategory?: (row: MenuItemRow, category: string) => void;
  categories?: string[];
}

interface MenuItemColumnOptions {
  /** When true, adds expander + Recipe ID column for panel view */
  expandable?: boolean;
  callbacks?: MenuItemRowCallbacks;
  /** When true, prepends a checkbox column for multi-row selection */
  selectable?: boolean;
}

/**
 * Returns columns for the Menu Items tab.
 * - **List view** (`expandable: false`): Image, Name (with badge + code), Category, Price, Cost
 * - **Panel view** (`expandable: true`): Expander, Recipe ID, Image, Name, Qty, Unit, Prep Notes, Unit Cost, Allergens
 */
export function getMenuItemColumns(
  i18n: I18n,
  options: MenuItemColumnOptions = {},
): ColumnDef<MenuItemRow, unknown>[] {
  const cols: ColumnDef<MenuItemRow, unknown>[] = [];

  if (options.selectable) {
    cols.push(makeSelectColumn<MenuItemRow>());
  }

  if (options.expandable) {
    cols.push({
      id: "expander",
      header: "",
      cell: ({ row }) => <ExpandCell row={row} />,
      enableSorting: false,
      enableHiding: false,
      size: 40,
    });

    cols.push({
      accessorKey: "recipeCode",
      meta: { label: i18n._(msg`Recipe ID`) },
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={i18n._(msg`Recipe ID`)} />
      ),
      cell: ({ getValue }) => <TextCell value={getValue() as string} mono />,
    });
  }

  cols.push(
    {
      accessorKey: "imageUrl",
      meta: { label: i18n._(msg`Image`) },
      header: i18n._(msg`Image`),
      cell: ({ row }) => (
        <ImageCell
          src={row.original.imageUrl}
          alt={row.original.name}
          size={44}
          rounded="lg"
        />
      ),
      enableSorting: false,
      size: 60,
    },
    {
      accessorKey: "name",
      meta: { label: i18n._(msg`Name`) },
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={i18n._(msg`Name`)} />
      ),
      cell: ({ row }) => {
        if (options.expandable) {
          return (
            <span className="font-medium text-sm text-(--color-text-primary)">
              {row.original.name}
            </span>
          );
        }
        return (
          <div className="flex flex-col gap-0.5">
            <div className="flex items-center gap-2">
              <span className="font-medium text-sm text-(--color-text-primary)">
                {row.original.name}
              </span>
              {row.original.status === "Published" ? (
                <Badge className="bg-(--tag-blue) text-(--color-action-fg) text-[11px] px-1.5 py-0.5">
                  {i18n._(msg`Published`)}
                </Badge>
              ) : row.original.status === "Archived" ? (
                <Badge
                  variant="outline"
                  className="text-[11px] px-1.5 py-0.5 text-(--color-text-secondary)"
                >
                  {i18n._(msg`Archived`)}
                </Badge>
              ) : (
                <Badge
                  variant="secondary"
                  className="text-[11px] px-1.5 py-0.5"
                >
                  {i18n._(msg`Draft`)}
                </Badge>
              )}
            </div>
            {row.original.recipeCode ? (
              <span className="text-xs text-(--color-text-secondary)">
                {row.original.recipeCode}
              </span>
            ) : null}
          </div>
        );
      },
    },
  );

  if (options.expandable) {
    // Panel columns: Qty, Unit, Prep Notes, Unit Cost, Allergens
    cols.push(
      {
        accessorKey: "targets",
        meta: { label: i18n._(msg`Qty`) },
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title={i18n._(msg`Qty`)} />
        ),
        cell: ({ getValue }) => <TextCell value={getValue() as string} />,
      },
      {
        accessorKey: "station",
        meta: { label: i18n._(msg`Unit`) },
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title={i18n._(msg`Unit`)} />
        ),
        cell: ({ getValue }) => <TextCell value={getValue() as string} />,
      },
      {
        accessorKey: "prepTime",
        meta: { label: i18n._(msg`Prep Notes`) },
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title={i18n._(msg`Prep Notes`)} />
        ),
        cell: ({ getValue }) => (
          <TextCell value={getValue() as string} muted truncate maxWidth={160} />
        ),
      },
      {
        accessorKey: "unitCost",
        meta: { label: i18n._(msg`Unit Cost`) },
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title={i18n._(msg`Unit Cost`)} />
        ),
        cell: ({ getValue }) => {
          const raw = getValue() as number | string | null | undefined;
          const n = raw != null ? Number(raw) : null;
          const hasValue = n != null && !isNaN(n);
          return <TextCell value={hasValue ? `$${n!.toFixed(2)}` : null} muted={!hasValue} />;
        },
      },
      {
        accessorKey: "allergens",
        meta: { label: i18n._(msg`Allergens`) },
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title={i18n._(msg`Allergens`)} />
        ),
        cell: ({ getValue }) => <TextCell value={getValue() as string} />,
      },
    );
  } else {
    // List columns: Category, Price, Cost
    cols.push(
      {
        // Ids match the API's sortable MenuItem fields, so the header sorts
        // server-side instead of sending a row key the API has no column for.
        id: "category",
        accessorFn: (row) => row.category,
        meta: { label: i18n._(msg`Category`) },
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title={i18n._(msg`Category`)} />
        ),
        cell: ({ getValue }) => <TextCell value={getValue() as string} muted />,
      },
      {
        // The row's `unitCost` carries the menu item's selling price.
        id: "price",
        accessorFn: (row) => row.unitCost,
        meta: { label: i18n._(msg`Price`) },
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title={i18n._(msg`Price`)} />
        ),
        cell: ({ getValue }) => {
          const n = getValue() as number | null | undefined;
          return <TextCell value={n != null ? `$${n.toFixed(2)}` : null} muted={n == null} />;
        },
      },
      {
        // Computed by the API from the plated recipe, so it has no column to
        // sort by. Blank, never $0.00, when there is nothing to cost.
        id: "cost",
        accessorFn: (row) => row.costPerPortion,
        meta: { label: i18n._(msg`Cost`) },
        header: i18n._(msg`Cost`),
        cell: ({ row }) => {
          const n = row.original.costPerPortion;
          if (n == null) return <TextCell value={null} muted />;
          return (
            <div className="flex flex-col">
              <TextCell value={`$${n.toFixed(2)}`} muted />
              {row.original.costIsComplete === false ? (
                <span className="text-xs text-(--color-text-secondary)">
                  {i18n._(msg`Minimum, recipe costing is incomplete`)}
                </span>
              ) : null}
            </div>
          );
        },
        enableSorting: false,
      },
    );
  }

  cols.push({
    id: "actions",
    cell: ({ row }) => {
      const cb = options.callbacks;
      if (!cb) return <ActionsCell items={[]} />;
      const rec = row.original;
      return (
        <ActionsCell
          items={[
            {
              label: i18n._(msg`Edit item`),
              onClick: () => cb.onEdit(rec),
            },
            ...(cb.onPublish || cb.onUnpublish
              ? [
                  {
                    label: i18n._(msg`Change item status`),
                    subItems: [
                      ...(rec.status !== "Published" && cb.onPublish
                        ? [{ label: i18n._(msg`Publish`), onClick: () => cb.onPublish!(rec) }]
                        : []),
                      ...(rec.status !== "Draft" && cb.onUnpublish
                        ? [{ label: i18n._(msg`Move to draft`), onClick: () => cb.onUnpublish!(rec) }]
                        : []),
                    ],
                  },
                ]
              : []),
            ...(cb.categories && cb.categories.length > 0 && cb.onChangeCategory
              ? [
                  {
                    label: i18n._(msg`Change category`),
                    subItems: cb.categories.map((c) => ({
                      label: c,
                      onClick: () => cb.onChangeCategory!(rec, c),
                    })),
                  },
                ]
              : []),
            {
              label: i18n._(msg`Delete item`),
              destructive: true,
              onClick: () => cb.onDelete(rec),
            },
          ]}
        />
      );
    },
    enableSorting: false,
    enableHiding: false,
    size: 48,
  });

  // No callbacks means the viewer may not edit or delete, so there is nothing
  // for a row menu to offer and no dead button to show.
  return options.callbacks ? cols : cols.filter((col) => col.id !== "actions");
}
